import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NbFormFieldModule, NbIconModule, NbInputModule, NbSpinnerModule } from '@nebular/theme';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { contemNormalizado, normalizarTexto } from '../../utils/normalizar-texto';

/** Opção exibida pelo autocomplete. O `id` é o que viaja; o `rotulo`, o que se lê. */
export interface OpcaoAutocomplete {
  id: string;
  rotulo: string;
}

/**
 * Campo de seleção de registro de domínio (`FRM-01`), num componente só.
 *
 * O projeto tinha três implementações disto: um `nb-select` com caixa de busca por cima, que
 * não é autocomplete, e dois painéis escritos à mão nas telas. `STK-07` — padrão que depende
 * de cada pessoa lembrar é padrão que já foi quebrado.
 *
 * O que ele garante, e que nenhuma das três garantia inteira:
 *
 * - filtra sem acento e sem caixa (`FRM-03`), pela função compartilhada;
 * - **texto que não casa não vira valor** (`FRM-03`): ao sair do campo, digitação que não
 *   corresponde a nenhuma opção zera a seleção e acusa opção inválida, em vez de deixar o
 *   registro apontando para um id que ninguém escolheu;
 * - mostra indicador enquanto carrega e "sem resultados" como item desabilitado (`FRM-03`);
 * - filtra no servidor quando o chamador pede (`FRM-04`), com espera entre teclas — o filtro
 *   em memória sobre uma página carregada é o que faz o campo dizer "sem resultados" para um
 *   registro que existe, a partir do primeiro que ficou fora da página.
 */
@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule, NbFormFieldModule, NbInputModule, NbIconModule, NbSpinnerModule],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
})
export class AutocompleteComponent implements OnChanges, OnDestroy {
  /** Opções conhecidas. Com `buscaNoServidor`, é o resultado da última busca. */
  @Input() opcoes: OpcaoAutocomplete[] = [];

  @Input() selecionadoId = '';
  @Output() selecionadoIdChange = new EventEmitter<string>();

  @Input() rotulo = '';
  @Input() placeholder = 'Digite para buscar…';

  /** Oferece uma opção que representa "sem recorte". */
  @Input() permiteTodos = false;
  @Input() rotuloTodos = 'Todos';

  /** Indicador de carregamento, controlado pela tela. */
  @Input() carregando = false;

  @Input() msgSemResultado = 'Nenhum resultado';

  /** Campo que só faz sentido depois de outra escolha (ex.: a turma) nasce desabilitado. */
  @Input() desabilitado = false;

  /**
   * `FRM-04` — quando verdadeiro o componente **não** filtra em memória: emite `buscar` com o
   * termo e espera a tela devolver `opcoes`.
   */
  @Input() buscaNoServidor = false;
  @Output() buscar = new EventEmitter<string>();

  texto = '';
  aberto = false;
  opcaoInvalida = false;
  filtradas: OpcaoAutocomplete[] = [];

  private readonly digitacao = new Subject<string>();
  private readonly sub: Subscription;
  private fechamento?: ReturnType<typeof setTimeout>;

  constructor() {
    this.sub = this.digitacao
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((termo) => this.buscar.emit(termo));
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['opcoes'] || ch['permiteTodos']) {
      this.aplicarFiltro();
    }
    if (ch['selecionadoId']) {
      this.sincronizarTextoComSelecao();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.fechamento) {
      clearTimeout(this.fechamento);
    }
  }

  aoFocar(): void {
    if (this.desabilitado) {
      return;
    }
    this.aberto = true;
    this.aplicarFiltro();
  }

  aoDigitar(): void {
    this.aberto = true;
    this.opcaoInvalida = false;
    if (this.buscaNoServidor) {
      this.digitacao.next(this.texto.trim());
      return;
    }
    this.aplicarFiltro();
  }

  /**
   * O fechamento espera o clique na opção acontecer — o `mousedown` do item dispara antes do
   * `blur`, mas o `click` não.
   */
  aoSair(): void {
    this.fechamento = setTimeout(() => {
      this.aberto = false;
      this.validarTextoDigitado();
    }, 150);
  }

  selecionar(opcao: OpcaoAutocomplete | null): void {
    this.opcaoInvalida = false;
    this.aberto = false;
    const id = opcao?.id ?? '';
    this.texto = opcao?.rotulo ?? (this.permiteTodos ? this.rotuloTodos : '');
    if (id !== this.selecionadoId) {
      this.selecionadoId = id;
      this.selecionadoIdChange.emit(id);
    }
  }

  /** Rótulo do que está escolhido, por extenso — o input mostra só o que couber. */
  get rotuloSelecionado(): string {
    return this.opcaoSelecionada()?.rotulo ?? '';
  }

  trackById = (_: number, o: OpcaoAutocomplete): string => o.id;

  private aplicarFiltro(): void {
    const lista = this.opcoes ?? [];
    if (this.buscaNoServidor) {
      this.filtradas = [...lista];
      return;
    }
    const termo = this.textoEhRotuloDeSelecao() ? '' : this.texto;
    this.filtradas = lista.filter((o) => contemNormalizado(o.rotulo, termo));
  }

  /**
   * Enquanto o campo mostra exatamente o rótulo do que já está escolhido, ele não é termo de
   * busca — senão abrir o campo já escolhido mostraria uma opção só.
   */
  private textoEhRotuloDeSelecao(): boolean {
    const atual = this.opcaoSelecionada();
    const rotuloAtual = atual?.rotulo ?? (this.permiteTodos ? this.rotuloTodos : '');
    return !!rotuloAtual && normalizarTexto(this.texto) === normalizarTexto(rotuloAtual);
  }

  private opcaoSelecionada(): OpcaoAutocomplete | undefined {
    return (this.opcoes ?? []).find((o) => o.id === this.selecionadoId);
  }

  private sincronizarTextoComSelecao(): void {
    const atual = this.opcaoSelecionada();
    if (atual) {
      this.texto = atual.rotulo;
      this.opcaoInvalida = false;
    } else if (!this.selecionadoId) {
      this.texto = this.permiteTodos ? this.rotuloTodos : '';
    }
  }

  /** `FRM-03` — texto livre não vira valor. */
  private validarTextoDigitado(): void {
    const digitado = this.texto.trim();

    if (!digitado) {
      this.selecionar(this.permiteTodos ? null : null);
      return;
    }

    const casa = (this.opcoes ?? []).find(
      (o) => normalizarTexto(o.rotulo) === normalizarTexto(digitado),
    );
    if (casa) {
      this.selecionar(casa);
      return;
    }

    if (this.permiteTodos && normalizarTexto(digitado) === normalizarTexto(this.rotuloTodos)) {
      this.selecionar(null);
      return;
    }

    // Não casou: a seleção anterior cai junto, para o formulário não guardar um id que
    // ninguém escolheu.
    this.opcaoInvalida = true;
    if (this.selecionadoId) {
      this.selecionadoId = '';
      this.selecionadoIdChange.emit('');
    }
  }
}
